import { ymd } from "./scheduleDateUtils";

export function norm(v) {
  return String(v || "").trim().toLowerCase();
}

export function getEmployeeName(emp) {
  return emp?.name || emp?.displayName || emp?.email || "Unknown";
}

export function getEmployeePosition(emp) {
  return emp?.position || emp?.title || emp?.role || emp?.basePosition || emp?.providerLevel || "";
}

export function isNewStaff(person) {
  if (!person) return false;
  const v = (person.workedBefore || "").toString().trim().toLowerCase();
  return v === "no" || v === "";
}

export function getEmployeeSearchText(emp) {
  return norm(
    [
      emp?.position,
      emp?.basePosition,
      emp?.providerLevel,
      emp?.title,
      emp?.role,
      emp?.roleLabel,
      emp?.classification,
      emp?.licenseLevel,
      emp?.credential,
      emp?.credentials,
      emp?.license,
    ]
      .filter(Boolean)
      .join(" ")
  );
}

export function getPositionNorm(emp) {
  return getEmployeeSearchText(emp);
}

export function isDirector(emp) {
  return /\bdirector\b/.test(getPositionNorm(emp));
}

export function isManager(emp) {
  return /\bmanager\b/.test(getPositionNorm(emp));
}

export function isSupervisor(emp) {
  return /\bsupervisor\b/.test(getPositionNorm(emp));
}

export function isDispatcher(emp) {
  return /\bdispatcher\b/.test(getPositionNorm(emp));
}

export function isPhysician(emp) {
  const p = getPositionNorm(emp);
  return /\bphysician\b|\bdoctor\b|\bmd\b|\bdo\b/.test(p);
}

export function isAPRN(emp) {
  const p = getPositionNorm(emp);
  return (
    /\baprn\b/.test(p) ||
    /\bnurse practitioner\b/.test(p) ||
    /\bnp\b/.test(p) ||
    /\bcrna\b/.test(p) ||
    /\bcnm\b/.test(p) ||
    /\bcns\b/.test(p)
  );
}

export function isPA(emp) {
  const p = getPositionNorm(emp);
  return (
    /\bphysician assistant\b/.test(p) ||
    /\bpa-c\b/.test(p) ||
    /\bpa\b/.test(p)
  );
}

export function isRN(emp) {
  const p = getPositionNorm(emp);
  return /\brn\b|\bregistered nurse\b/.test(p);
}

export function isParamedic(emp) {
  return /\bparamedic\b/.test(getPositionNorm(emp));
}

export function isAEMT(emp) {
  const p = getPositionNorm(emp);
  return /\baemt\b|\badvanced emt\b/.test(p);
}

export function isEMT(emp) {
  const p = getPositionNorm(emp);
  return (
    (/\bemt\b|\bemt-b\b|\bemt basic\b|\bemergency medical technician\b/.test(p)) &&
    !/\baemt\b|\badvanced emt\b/.test(p) &&
    !/\bparamedic\b/.test(p)
  );
}

export function isEMR(emp) {
  const p = getPositionNorm(emp);
  return /\bemr\b|\bemergency medical responder\b/.test(p);
}

export function isTech(emp) {
  const p = getPositionNorm(emp);
  return /\btech\b|\btechnician\b/.test(p);
}

export function isResident(emp) {
  return /\bresident\b/.test(getPositionNorm(emp));
}

export function isTentEligible(emp) {
  return isEMT(emp) || isAEMT(emp) || isParamedic(emp);
}

export function getAssignmentPreferences(emp) {
  return Array.isArray(emp?.assignment) ? emp.assignment.map(norm) : [];
}

export function hasNoPreference(emp) {
  return getAssignmentPreferences(emp).includes("no preference");
}

export function prefersBikeTeam(emp) {
  return getAssignmentPreferences(emp).includes("bike team");
}

export function prefersCartTeam(emp) {
  return getAssignmentPreferences(emp).includes("cart team");
}

export function prefersAidStation(emp) {
  return getAssignmentPreferences(emp).includes("on course aid station");
}

export function prefersMedicalHQ(emp) {
  const prefs = getAssignmentPreferences(emp);
  return (
    prefs.includes("medical headquarters") ||
    prefs.includes("medical headquaters")
  );
}

export function isCartEligible(emp) {
  return isEMT(emp) || isAEMT(emp) || isParamedic(emp);
}

export function isBikeEligible(emp) {
  return (isEMT(emp) || isAEMT(emp) || isParamedic(emp)) && prefersBikeTeam(emp);
}

export function isCartBikeEligible(emp) {
  return isCartEligible(emp);
}

export function isTransportEligible(emp) {
  return (
    isTech(emp) ||
    isEMR(emp) ||
    isEMT(emp) ||
    isAEMT(emp) ||
    isParamedic(emp)
  );
}

export function canRequestShiftForPosition(myPosRaw, shiftPosRaw) {
  const myPos = norm(myPosRaw);
  const shiftPos = norm(shiftPosRaw);

  if (!myPos) return false;
  if (!shiftPos) return false;

  if (
    shiftPos === "emt/aemt/paramedic" ||
    shiftPos === "emt/paramedic" ||
    shiftPos === "emt/aemt"
  ) {
    return ["emt", "aemt", "paramedic"].includes(myPos);
  }

  if (shiftPos === "paramedic") return myPos === "paramedic";
  if (shiftPos === "dispatcher") return myPos === "dispatcher";
  if (shiftPos === "physician") return myPos === "physician";
  if (shiftPos === "rn") return myPos === "rn";
  if (shiftPos === "aprn/pa") return ["aprn", "pa"].includes(myPos);
  if (shiftPos === "transport") {
    return ["emt", "aemt", "paramedic", "emr", "tech"].includes(myPos);
  }

  return myPos === shiftPos;
}

export function canEmployeeRequestShift(emp, shift) {
  const roleKey = String(shift?.roleKey || shift?.assignment || "").trim();
  const lowerRole = roleKey.toLowerCase();
  const shiftPosition = norm(shift?.position);

  // Never show command / supervisor roles to self-serve users
  const blockedExactRoles = new Set([
    "incident command",
    "unified command",
    "planning",
    "logistics",
    "ops manager",
    "ops supervisor 1",
    "ops supervisor 2",
    "triage supervisor",
  ]);

  if (blockedExactRoles.has(lowerRole)) {
    return false;
  }

  // Dispatch
  if (lowerRole === "dispatcher" || lowerRole === "dispatcher 2") {
    return isDispatcher(emp);
  }

  // Tent openings: EMT / AEMT / Paramedic only
  if (/^tent\s+\d+/i.test(roleKey)) {
    return isEMT(emp) || isAEMT(emp) || isParamedic(emp);
  }

  // Cart openings: EMT / AEMT / Paramedic only
  if (lowerRole.includes("cart")) {
    return isEMT(emp) || isAEMT(emp) || isParamedic(emp);
  }

  // Bike openings: EMT / AEMT / Paramedic only
  if (lowerRole.includes("bike")) {
    return isEMT(emp) || isAEMT(emp) || isParamedic(emp);
  }

  // Transport openings: EMT / AEMT / Paramedic only
  if (lowerRole.includes("transport")) {
    return isEMT(emp) || isAEMT(emp) || isParamedic(emp);
  }

  // Tech openings
  if (shiftPosition === "tech" || lowerRole === "tech") {
    return isTech(emp);
  }

  // Physician openings
  if (shiftPosition === "physician" || lowerRole.startsWith("physician")) {
    return isPhysician(emp);
  }

  // APRN / PA openings
  if (shiftPosition === "aprn/pa" || lowerRole.startsWith("aprn/pa")) {
    return isAPRN(emp) || isPA(emp);
  }

  // RN openings
  if (shiftPosition === "rn" || lowerRole.startsWith("rn")) {
    return isRN(emp);
  }

  // EMT/AEMT openings in HQ:
  // EMT, AEMT, and Paramedic can all see these
  if (
    shiftPosition === "emt/aemt" ||
    shiftPosition === "emt/aemt/paramedic" ||
    lowerRole.startsWith("emt/aemt")
  ) {
    return isEMT(emp) || isAEMT(emp) || isParamedic(emp);
  }

  // Paramedic openings
  if (shiftPosition === "paramedic" || lowerRole.startsWith("paramedic")) {
    return isParamedic(emp);
  }

  // Fallback to exact position matching only
  return canRequestShiftForPosition(getEmployeePosition(emp), shift?.position);
}

export function parseAvailabilityEntryToISO(value) {
  if (!value) return "";

  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return ymd(parsed);
    }
    return "";
  }

  if (value?.toDate && typeof value.toDate === "function") {
    const d = value.toDate();
    if (!Number.isNaN(d.getTime())) {
      return ymd(d);
    }
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return ymd(value);
  }

  return "";
}

export function getEmployeeAvailabilityISOs(emp) {
  const raw = Array.isArray(emp?.availability) ? emp.availability : [];
  return raw.map(parseAvailabilityEntryToISO).filter(Boolean);
}

export function isEmployeeAvailableForDate(emp, isoDate) {
  const hasAvailabilityField = Array.isArray(emp?.availability);
  const dates = getEmployeeAvailabilityISOs(emp);
  if (!hasAvailabilityField) return true;
  return dates.includes(isoDate);
}